import React, { useRef, useEffect } from "react"
import { useAccessContext } from "@/contexts/AccessContext"
import { useMap } from "react-leaflet"
import GeoJsonLayer from "@/components/map/GeoJsonLayer"
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx"
import L from "leaflet"
import PilotStationIcon from "@mui/icons-material/LocalParkingRounded"
import "leaflet.markercluster"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import ReactDOMServer from "react-dom/server"
import { coreGet } from "@/services/coreApi"

// Function to filter features based on their visibility within the current map viewport.
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds()
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry
    // Determine visibility based on geometry type:
    // LineString: check if any segment point is within bounds
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) => bounds.contains([coord[1], coord[0]]))
      // MultiLineString: check each line segment
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) => line.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Point: check if the point is within bounds
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      // Polygon: check each ring coordinate
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) => ring.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Fallback: handle other geometry types generically
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      }
      return false
    }
  })
  return filteredFeatures
}

export default function PilotStations({
  cwPilotStationGeojson,
  setCwPilotStationGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const pilotStationsEnabled =
    switchGisLayersActive && toggleFlags.includes("PilotStations")

  const map = useMap()
  const suppressed = useGisSuppressed()
  const clusterRef = useRef(null)
  const [clusterVersion, setClusterVersion] = React.useState(0)
  const tooltipCountRef = useRef(0)

  const clearedThisCycleRef = useRef(false)
  const addedKeysRef = useRef(new Set())

  // Reset per-cycle flags when inputs change (prevents duplicate markers in cluster)
  useEffect(() => {
    clearedThisCycleRef.current = false
    addedKeysRef.current = new Set()
  }, [zoomLevel, dragged, resized, cwPilotStationGeojson, toggleFlags, switchGisLayersActive])

  useEffect(() => {
    if (!pilotStationsEnabled) return
    if (cwPilotStationGeojson !== null) return

    let cancelled = false
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/pilot-stations",
          accessContext,
          setAccessContext,
          ".core /geo/pilot-stations error="
        )
        if (!cancelled) {
          setCwPilotStationGeojson(data)
        }
      } catch (error) {
        console.error(error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [
    accessContext,
    cwPilotStationGeojson,
    pilotStationsEnabled,
    setAccessContext,
    setCwPilotStationGeojson,
  ])

  // Create/remove pilot station marker cluster when toggles change; clean up previous cluster
  useEffect(() => {
    if (clusterRef.current) {
      clusterRef.current.clearLayers()
      map.removeLayer(clusterRef.current)
      clusterRef.current = null
    }
    if (pilotStationsEnabled) {
      clusterRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        maxClusterRadius: (zoom) => Math.max(60 - (zoom - 6) * 6, 20),
        disableClusteringAtZoom: 11,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount()
          return L.divIcon({
            html: `<div style="
              background: rgba(128, 0, 128, 0.8);
              color: black;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 0 0 6px rgba(128, 0, 128, 0.25);
              font-weight: 500;
              font-size: 14px;
            ">${count}</div>`,
            className: "custom-cluster-icon",
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          })
        },
      })
      clusterRef.current.addTo(map)
      setClusterVersion((v) => v + 1)
    }
    return () => {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, pilotStationsEnabled, toggleFlags, switchGisLayersActive])

  useEffect(() => {
    if (!pilotStationsEnabled && cwPilotStationGeojson !== null) {
      setCwPilotStationGeojson(null)
    }
  }, [
    cwPilotStationGeojson,
    pilotStationsEnabled,
    setCwPilotStationGeojson,
  ])

  return !pilotStationsEnabled || cwPilotStationGeojson === null ? null : (
    <GeoJsonLayer
      key={[zoomLevel, dragged, resized, clusterVersion, suppressed]}
      data={filterViewportBoundingbox(map, cwPilotStationGeojson.features)}
      style={{ fillColor: "purple", color: "purple", weight: 2 }}
      onEachFeature={(feature, layer) => {
        if (clusterRef.current && !clearedThisCycleRef.current) {
          clusterRef.current.clearLayers()
          clearedThisCycleRef.current = true
        }
        layer.bindPopup(
          `<div><strong>${feature.properties.name || "Pilot Station"}</strong>${
            feature.properties.category ? `<br/>${feature.properties.category}` : ""
          }${feature.properties.comm_channel ? `<br/>${feature.properties.comm_channel}` : ""}${
            feature.properties.information ? `<br/>${feature.properties.information}` : ""
          }</div>`
        )
        if (feature.geometry?.coordinates) {
          const [lng, lat] = feature.geometry.coordinates
          const fid = feature?.id || feature?.properties?.id || feature?.properties?.uuid || feature?.properties?.gid
          const fKey = fid != null ? String(fid) : `${lat.toFixed(6)},${lng.toFixed(6)}`
          if (addedKeysRef.current.has(fKey)) return
          addedKeysRef.current.add(fKey)
          const iconHtml = ReactDOMServer.renderToStaticMarkup(
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(128, 0, 128, 0.12)",
                border: "2px solid rgba(128, 0, 128, 0.55)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backdropFilter: "saturate(110%)",
              }}
            >
              <PilotStationIcon style={{ color: "purple" }} />
            </div>
          )
          const customIcon = L.divIcon({
            html: iconHtml,
            className: "pilot-icon",
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })
          const marker = L.marker([lat, lng], { icon: customIcon, interactive: !suppressed })
          if (!suppressed) marker.bindPopup(layer.getPopup().getContent())
          clusterRef.current?.addLayer(marker)
        }
      }}
      pointToLayer={(feature, latlng) =>
        L.circleMarker(latlng, { radius: 0, opacity: 0, fillOpacity: 0, stroke: false })
      }
      interactive
      transparent
    />
  )
}
